import { RefObject, useCallback, useRef, useState } from "react";
import { PhoneVerifiedSubmissionResult, PendingVerification } from "src/customer/phone_verified_submission";
import { verifyPhoneNumber, VerifyPhoneNumberResult } from "src/customer/verify_phone_number";

type VerificationStep = PendingVerification & {
  formRef: RefObject<HTMLFormElement | null>;
  isPending: boolean;
  error: string | undefined;
  clearError: () => void;
  submit: () => void;
  cancel: () => void;
  onChangePhoneNumber: () => void;
};

const SUBMISSION_FAILED_MESSAGE = "Error trying to sign up. Please try again later";
const VERIFICATION_FAILED_MESSAGE = "Error trying to verify your phone number. Please try again later";
const NEW_VERIFICATION_CODE_SENT_MESSAGE = "We've sent you a new verification code, please enter it to continue";

const textValueOf = (formData: FormData | null, field: string) => {
  const value = formData?.get(field);

  return typeof value === "string" ? value : undefined;
}

const usePhoneVerifiedSubmission = ({
  submit,
  onSuccess,
}: {
  submit: (formData: FormData) => Promise<PhoneVerifiedSubmissionResult>;
  onSuccess: () => void;
}) => {
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedDetails = useRef<FormData | null>(null);

  const [pendingVerification, setPendingVerification] = useState<PendingVerification | null>(null);
  const [verificationError, setVerificationError] = useState<string | undefined>(undefined);
  const [isVerifying, setIsVerifying] = useState(false);
  const isVerificationInProgress = useRef(false);
  const verificationCodeFormRef = useRef<HTMLFormElement>(null);

  const submittedValue = (field: string) => textValueOf(submittedDetails.current, field);

  const clearErrors = () => setErrors({});
  const clearVerificationError = () => setVerificationError(undefined);

  const finishVerifying = useCallback(() => {
    isVerificationInProgress.current = false;
    setIsVerifying(false);
  }, []);

  const returnToDetailsStep = useCallback(() => {
    finishVerifying();
    setVerificationError(undefined);
    setPendingVerification(null);
  }, [finishVerifying]);

  const showVerifyStep = (verification: PendingVerification) => {
    setPendingVerification(verification);
    setVerificationError(undefined);
  }

  const handleSubmissionResponse = (response: PhoneVerifiedSubmissionResult) => {
    switch (response.status) {
      case "created":
        onSuccess();
        break;
      case "verificationRequired":
        setIsSubmitting(false);
        showVerifyStep(response.verification);
        break;
      case "invalid":
        setErrors(response.errors);
        setIsSubmitting(false);
        break;
    }
  }

  const handleSubmissionError = () => {
    setErrors({ base: [SUBMISSION_FAILED_MESSAGE] });
    setIsSubmitting(false);
  }

  const submitDetails = (formData: FormData) => {
    submittedDetails.current = formData;
    setErrors({});
    setIsSubmitting(true);

    submit(formData)
      .then(handleSubmissionResponse)
      .catch(handleSubmissionError);
  }

  const handleSubmissionAfterVerificationResponse = useCallback((response: PhoneVerifiedSubmissionResult) => {
    switch (response.status) {
      case "created":
        onSuccess();
        break;
      case "verificationRequired":
        finishVerifying();
        setPendingVerification(response.verification);
        setVerificationError(NEW_VERIFICATION_CODE_SENT_MESSAGE);
        break;
      case "invalid":
        setErrors(response.errors);
        returnToDetailsStep();
        break;
    }
  }, [onSuccess, finishVerifying, returnToDetailsStep]);

  const handleVerifyPhoneNumberResponse = useCallback(({ verified, error }: VerifyPhoneNumberResult) => {
    if (!verified || !submittedDetails.current) {
      setVerificationError(error ?? VERIFICATION_FAILED_MESSAGE);
      finishVerifying();
      return;
    }

    return submit(submittedDetails.current).then(handleSubmissionAfterVerificationResponse);
  }, [submit, finishVerifying, handleSubmissionAfterVerificationResponse]);

  const submitVerificationCode = useCallback(() => {
    if (!verificationCodeFormRef.current || isVerificationInProgress.current) return;

    isVerificationInProgress.current = true;
    setIsVerifying(true);

    const verificationCode = textValueOf(new FormData(verificationCodeFormRef.current), "verification_code") ?? "";

    verifyPhoneNumber(verificationCode)
      .then(handleVerifyPhoneNumberResponse)
      .catch(() => {
        setVerificationError(VERIFICATION_FAILED_MESSAGE);
        finishVerifying();
      });
  }, [finishVerifying, handleVerifyPhoneNumberResponse]);

  const verification: VerificationStep | null = pendingVerification && {
    ...pendingVerification,
    formRef: verificationCodeFormRef,
    isPending: isVerifying,
    error: verificationError,
    clearError: clearVerificationError,
    submit: submitVerificationCode,
    cancel: returnToDetailsStep,
    onChangePhoneNumber: returnToDetailsStep,
  };

  return {
    details: { errors, isSubmitting, onSubmit: submitDetails, clearErrors },
    submittedValue,
    verification,
  };
}

export { usePhoneVerifiedSubmission, VerificationStep };
